import React from 'react';
import styled from 'App/styled';
import { FormattedMessage } from '@nykaa/intl-shared';
import { SOLD_OUT } from 'constants/strings';
import { Variant } from 'shared/store/product/types';
import { getOptimisedImageUrl } from '@nykaa/image-utils';
import DarkCheckCircle from '@nykaa/ui-components/Icons/dark-check-circle';

interface VariantListProps {
  variants: Variant[];
  changeVariant: Function;
  bestSellersOnly: boolean;
  selectedProductId: string;
  optimizedImageUrlEnabled: boolean;
}

const Wrapper = styled.div``;

const ListWrapper = styled.ul`
  overflow: hidden;
  list-style-type: none;
  padding: 0 10px;
  margin-top: 10px;
`;

const Item = styled.li`
  width: 50%;
  padding-right: 5px;
  padding-left: 5px;
  text-align: left;
  vertical-align: top;
  display: inline-block;
  margin: 10px 0px;
`;

const Label = styled.label`
  display: flex;
`;

const VariantName = styled.span`
  display: inline-block;
  text-overflow: ellipsis;
  overflow: hidden;
  vertical-align: middle;
  width: inherit;
  ${({ theme }) => theme.typography.bodyMedium};
  color: ${({ theme }) => theme.colors.textSecondary};

  &.active {
    ${({ theme }) => theme.typography.subTitleMedium};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;
const Shade = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  margin-right: 10px;

  &:focus {
    outline: none;
  }
`;

// We want to add custom TickIcon when we change the shade
const ShadeImg = styled.span`
  display: inline-block;
  overflow: hidden;
  border-radius: 4px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
  width: 50px;
  height: 50px;
  position: relative;

  img {
    width: 100%;
    height: 100%;
  }

  &.active {
    border: 1px solid #fff;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.5), 0 2px 4px 0 rgba(0, 0, 0, 0.7),
      0 -2px 4px 0 rgba(0, 0, 0, 0.5);
  }

  &.oos {
    & :after {
      content: '';
      background-color: #fff;
      border-radius: 2px;
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.5);
      width: 44px;
      height: 2px;
      transform: rotate(45deg);
      position: absolute;
      top: 25px;
      left: 4px;
    }
  }
`;

const TickIconWrapper = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
`;

const OutOfStock = styled.span`
  display: block;
  color: red;
  ${({ theme }) => theme.typography.bodySmall};
`;

const NameWrapper = styled.div`
  margin-top: 5px;
`;

const VariantList = ({
  variants,
  selectedProductId,
  changeVariant,
  bestSellersOnly = false,
  optimizedImageUrlEnabled,
}: VariantListProps) => (
  <Wrapper>
    <ListWrapper role="radiogroup">
      {variants.map((variant, index) => {
        const { shadeImage, variantName, quantity, isBestSeller, childId } =
          variant;
        if (bestSellersOnly && !isBestSeller) {
          return null;
        }
        const isVariantSelected = childId === selectedProductId;
        const isVariantOutOfStock = quantity === 0;

        let additionalClassForShade = isVariantSelected ? 'active' : '';

        const optimizedImage = getOptimisedImageUrl({
          url: shadeImage,
          width: 50,
          height: 50,
          newImageUrlEnabled: optimizedImageUrlEnabled,
        });

        if (isVariantOutOfStock) {
          additionalClassForShade += ' oos';
        }
        return (
          <Item key={`allshadeLi-${index}`}>
            <Label onClick={() => changeVariant(childId)}>
              <Shade
                type="button"
                key={childId}
                role="radio"
                aria-checked={isVariantSelected}
              >
                <ShadeImg className={additionalClassForShade}>
                  <img
                    src={`${optimizedImage?.src}`}
                    srcSet={`${optimizedImage?.srcSet}`}
                    alt={`${variantName}-shade`}
                    loading="lazy"
                  />
                  {isVariantSelected && (
                    <TickIconWrapper>
                      <DarkCheckCircle />
                    </TickIconWrapper>
                  )}
                </ShadeImg>
              </Shade>
              <NameWrapper>
                <VariantName className={additionalClassForShade}>
                  {variantName}
                </VariantName>
                {isVariantOutOfStock && (
                  <OutOfStock>
                    <FormattedMessage id={SOLD_OUT} />
                  </OutOfStock>
                )}
              </NameWrapper>
            </Label>
          </Item>
        );
      })}
    </ListWrapper>
  </Wrapper>
);
export default VariantList;
